/**
 * Tipos de checklist item suportados.
 *
 * - `document` (default, ausente): upload tradicional de PDF.
 * - `external-form-respondi`: link para formulário externo da Respondi
 *   complementado por upload de PDF de respostas exportado.
 */
export type ChecklistItemKind = 'document' | 'external-form-respondi';

/**
 * Item de checklist associado a uma etapa da diligência
 */
export interface ChecklistItem {
  /** Chave única do item (usada para persistência) */
  key: string;
  /** Texto exibido ao usuário */
  label: string;
  /** Se verdadeiro, o item é obrigatório para avançar de etapa */
  required: boolean;
  /**
   * Tipo do item. Quando ausente, comporta-se como `document` (padrão).
   */
  kind?: ChecklistItemKind;
  /**
   * URL do formulário externo a ser aberto em nova aba.
   * Obrigatório quando `kind === 'external-form-respondi'`.
   */
  externalUrl?: string;
}

// =============================================================================
// ETAPAS INTERNAS (workflow real — 6 etapas)
// =============================================================================

/**
 * Etapas possíveis de uma diligência, na ordem de progressão.
 * Este é o tipo canônico — todos os módulos devem importar daqui.
 */
export type DiligenciaEtapa =
  | 'nova_solicitacao'
  | 'viabilidade_tecnica_economica'
  | 'viabilidade_juridica'
  | 'viabilidade_contabil'
  | 'viabilidade_mercadologica'
  | 'relatorio';

// =============================================================================
// ETAPAS VISUAIS DO INCORPORADOR (agrupamento simplificado — 4 etapas)
// =============================================================================

/**
 * Etapas visuais exibidas ao incorporador.
 * Agrupa as 6 etapas internas em 4 etapas simplificadas.
 */
export type EtapaVisualIncorporador =
  | 'nova_solicitacao'
  | 'viabilidade_tecnica_juridica'
  | 'viabilidade_contabil_mercado'
  | 'relatorio';

export interface EtapaVisualConfig {
  key: EtapaVisualIncorporador;
  label: string;
  /** Etapas internas agrupadas nesta etapa visual */
  etapasInternas: DiligenciaEtapa[];
}

/**
 * Configuração das 4 etapas visuais do incorporador, com mapeamento
 * para as etapas internas correspondentes.
 */
export const ETAPAS_VISUAIS_INCORPORADOR: EtapaVisualConfig[] = [
  {
    key: 'nova_solicitacao',
    label: 'Nova solicitação',
    etapasInternas: ['nova_solicitacao'],
  },
  {
    key: 'viabilidade_tecnica_juridica',
    label: 'Pré análise',
    etapasInternas: ['viabilidade_tecnica_economica', 'viabilidade_juridica'],
  },
  {
    key: 'viabilidade_contabil_mercado',
    label: 'Análise conclusiva',
    etapasInternas: ['viabilidade_contabil', 'viabilidade_mercadologica'],
  },
  {
    key: 'relatorio',
    label: 'Relatório',
    etapasInternas: ['relatorio'],
  },
];

/**
 * Labels das etapas visuais do incorporador.
 */
export const ETAPA_VISUAL_LABELS: Record<EtapaVisualIncorporador, string> = {
  nova_solicitacao: 'Nova solicitação',
  viabilidade_tecnica_juridica: 'Pré análise',
  viabilidade_contabil_mercado: 'Análise conclusiva',
  relatorio: 'Relatório',
};

/**
 * Mapeia uma etapa interna para a etapa visual correspondente do incorporador.
 */
export function mapEtapaToVisual(etapa: DiligenciaEtapa): EtapaVisualIncorporador {
  const visual = ETAPAS_VISUAIS_INCORPORADOR.find((v) =>
    v.etapasInternas.includes(etapa)
  );
  return visual?.key ?? 'nova_solicitacao';
}

// =============================================================================
// ESPECIALIDADES ADMINISTRATIVAS
// =============================================================================

/**
 * Especialidades possíveis para admins de diligência.
 * NULL no banco = admin principal com acesso total.
 */
export type DiligenciaEspecialidade = 'juridico' | 'tecnica_economica' | 'contabil';

/**
 * Mapeamento de especialidade → etapas internas que o admin especializado pode acessar.
 */
export const ESPECIALIDADE_ETAPAS: Record<DiligenciaEspecialidade, DiligenciaEtapa[]> = {
  juridico: ['viabilidade_juridica'],
  tecnica_economica: ['viabilidade_tecnica_economica'],
  contabil: ['viabilidade_contabil'],
};

/**
 * Labels das especialidades administrativas.
 */
export const ESPECIALIDADE_LABELS: Record<DiligenciaEspecialidade, string> = {
  juridico: 'Jurídico',
  tecnica_economica: 'Técnica/Econômica',
  contabil: 'Contábil',
};

/**
 * Retorna as etapas que um admin pode acessar baseado na sua especialidade.
 * Se `especialidade` for null, retorna todas as etapas (admin principal).
 */
export function getEtapasPermitidas(
  especialidade: DiligenciaEspecialidade | null
): DiligenciaEtapa[] {
  if (!especialidade) return [...ETAPAS_ORDENADAS];
  return ESPECIALIDADE_ETAPAS[especialidade] ?? [...ETAPAS_ORDENADAS];
}

/**
 * Mapeamento de etapa → lista de itens de checklist esperados.
 *
 * As etapas `nova_solicitacao` e `relatorio` não possuem checklist de
 * documentos — a primeira é o envio inicial da solicitação e a última
 * é o relatório final produzido pela BravaForte.
 */
export const DILIGENCIA_CHECKLISTS: Record<DiligenciaEtapa, ChecklistItem[]> = {
  nova_solicitacao: [], // Sem checklist para o envio inicial

  viabilidade_tecnica_economica: [
    {
      key: 'viabilidade_tecnica_respondi',
      label: 'Viabilidade técnica (formulário externo)',
      required: true,
      kind: 'external-form-respondi',
      externalUrl: 'https://form.respondi.app/7wlAOFqD',
    },
    { key: 'matricula_onus', label: 'Matrícula de ônus atualizada', required: true },
    { key: 'guia_iptu', label: 'Guia de IPTU', required: true },
    { key: 'cartao_cnpj', label: 'Cartão CNPJ da SPE', required: true },
    { key: 'projeto_arquitetonico', label: 'Projeto arquitetônico', required: true },
    { key: 'memorial_descritivo', label: 'Memorial descritivo', required: false },
  ],

  viabilidade_juridica: [
    { key: 'contrato_social_spe', label: 'Contrato social da SPE', required: true },
    { key: 'certidoes_negativas', label: 'Certidões negativas', required: true },
    { key: 'alvara_construcao', label: 'Alvará de construção', required: false },
    { key: 'art_rrt', label: 'ART/RRT', required: false },
  ],

  viabilidade_contabil: [
    { key: 'balanco_patrimonial', label: 'Balanço patrimonial', required: true },
    { key: 'dre_projetado', label: 'DRE projetado', required: true },
    { key: 'fluxo_caixa', label: 'Fluxo de caixa', required: true },
    { key: 'quadro_areas', label: 'Quadro de áreas', required: false },
  ],

  viabilidade_mercadologica: [
    { key: 'estudo_mercado', label: 'Estudo de mercado', required: true },
    { key: 'tabela_vendas', label: 'Tabela de vendas', required: true },
    { key: 'material_publicitario', label: 'Material publicitário', required: false },
  ],

  relatorio: [], // Relatório final produzido pela BravaForte, sem novos uploads
};

/**
 * Label legível por etapa para exibição na interface
 */
export const ETAPA_LABELS: Record<DiligenciaEtapa, string> = {
  nova_solicitacao: 'Nova solicitação',
  viabilidade_tecnica_economica: 'Viab. técnica/econômica',
  viabilidade_juridica: 'Viab. jurídica',
  viabilidade_contabil: 'Viab. contábil',
  viabilidade_mercadologica: 'Viab. mercadológica',
  relatorio: 'Relatório',
};

/**
 * Etapas em ordem cronológica de progressão da diligência
 */
export const ETAPAS_ORDENADAS: DiligenciaEtapa[] = [
  'nova_solicitacao',
  'viabilidade_tecnica_economica',
  'viabilidade_juridica',
  'viabilidade_contabil',
  'viabilidade_mercadologica',
  'relatorio',
];

// =============================================================================
// PROGRESSÃO DE GRUPO — STATUS POR ÁREA
// =============================================================================

/**
 * Status possíveis de uma área de viabilidade.
 */
export type AreaStatus = 'pendente' | 'em_analise' | 'concluida';

/**
 * Mapeamento de etapa interna → chave da coluna de status no banco.
 */
export const ETAPA_TO_AREA_STATUS_KEY: Record<string, string> = {
  viabilidade_tecnica_economica: 'area_tecnica_economica_status',
  viabilidade_juridica: 'area_juridica_status',
  viabilidade_contabil: 'area_contabil_status',
  viabilidade_mercadologica: 'area_mercadologica_status',
};

/**
 * Labels legíveis para os status de área.
 */
export const AREA_STATUS_LABELS: Record<AreaStatus, string> = {
  pendente: 'Pendente',
  em_analise: 'Em análise',
  concluida: 'Concluída',
};

/**
 * Interface para status de áreas de uma diligência.
 */
export interface DiligenciaAreaStatuses {
  area_tecnica_economica_status: AreaStatus;
  area_juridica_status: AreaStatus;
  area_contabil_status: AreaStatus;
  area_mercadologica_status: AreaStatus;
}

/**
 * Retorna o status de uma área específica a partir dos statuses da diligência.
 */
export function getAreaStatus(
  areaStatuses: DiligenciaAreaStatuses,
  etapa: DiligenciaEtapa
): AreaStatus | null {
  const key = ETAPA_TO_AREA_STATUS_KEY[etapa];
  if (!key) return null;
  return (areaStatuses as Record<string, AreaStatus>)[key] ?? 'pendente';
}

/**
 * Verifica se todas as áreas de um grupo visual estão concluídas.
 */
export function isGrupoVisualConcluido(
  grupoKey: EtapaVisualIncorporador,
  areaStatuses: DiligenciaAreaStatuses
): boolean {
  const grupo = ETAPAS_VISUAIS_INCORPORADOR.find((v) => v.key === grupoKey);
  if (!grupo) return false;

  // Grupos sem áreas de viabilidade (nova_solicitacao, relatorio) são sempre "concluídos"
  const etapasComArea = grupo.etapasInternas.filter(
    (e) => ETAPA_TO_AREA_STATUS_KEY[e]
  );
  if (etapasComArea.length === 0) return true;

  return etapasComArea.every((etapa) => {
    const status = getAreaStatus(areaStatuses, etapa);
    return status === 'concluida';
  });
}

/**
 * Verifica se o próximo grupo visual pode ser desbloqueado,
 * baseado na conclusão de todas as áreas do grupo atual.
 */
export function canAdvanceToNextGrupo(
  etapaAtual: DiligenciaEtapa,
  areaStatuses: DiligenciaAreaStatuses
): boolean {
  const grupoAtualKey = mapEtapaToVisual(etapaAtual);
  return isGrupoVisualConcluido(grupoAtualKey, areaStatuses);
}

/**
 * Retorna a primeira etapa do próximo grupo visual, ou null se for o último.
 * Usado para avançar `etapa_atual` quando o grupo atual fica completo.
 */
export function getNextGrupoFirstEtapa(
  etapaAtual: DiligenciaEtapa
): DiligenciaEtapa | null {
  const grupoAtualKey = mapEtapaToVisual(etapaAtual);
  const idx = ETAPAS_VISUAIS_INCORPORADOR.findIndex((v) => v.key === grupoAtualKey);
  if (idx < 0 || idx >= ETAPAS_VISUAIS_INCORPORADOR.length - 1) return null;
  const nextGrupo = ETAPAS_VISUAIS_INCORPORADOR[idx + 1];
  return nextGrupo.etapasInternas[0] ?? null;
}

/**
 * Retorna todas as etapas internas do grupo visual que contém a etapa dada.
 */
export function getEtapasDoGrupoAtual(etapaAtual: DiligenciaEtapa): DiligenciaEtapa[] {
  const grupoKey = mapEtapaToVisual(etapaAtual);
  const grupo = ETAPAS_VISUAIS_INCORPORADOR.find((v) => v.key === grupoKey);
  return grupo?.etapasInternas ?? [etapaAtual];
}

/**
 * Retorna a chave da coluna de status para a especialidade do admin.
 */
export function getAreaStatusKeysForEspecialidade(
  especialidade: DiligenciaEspecialidade
): string[] {
  const etapas = ESPECIALIDADE_ETAPAS[especialidade] ?? [];
  return etapas
    .map((e) => ETAPA_TO_AREA_STATUS_KEY[e])
    .filter(Boolean);
}

/**
 * Verifica se uma etapa de viabilidade tem um admin especializado responsável.
 * Se false, a área é de responsabilidade do admin principal.
 */
export function hasEspecialistaForEtapa(etapa: DiligenciaEtapa): boolean {
  return Object.values(ESPECIALIDADE_ETAPAS).some((etapas) =>
    etapas.includes(etapa)
  );
}

/**
 * Labels descritivos do responsável por cada área, para exibição no resumo.
 */
export function getAreaResponsavelLabel(etapa: DiligenciaEtapa): string {
  for (const [esp, etapas] of Object.entries(ESPECIALIDADE_ETAPAS)) {
    if (etapas.includes(etapa)) {
      return ESPECIALIDADE_LABELS[esp as DiligenciaEspecialidade];
    }
  }
  return 'Admin Principal';
}
